import React, { useState, useRef, useEffect, useMemo } from 'react';
import { usersService, rolePermissionsService, auditLogService } from '../services/firestoreService';
import { logoStorageService } from '../services/storageService';
import { useApp } from '../context/AppContext';
import { JOB_CARD_TEMPLATES, DEFAULT_JOB_CARD_LABELS, generateJobCardHTML, SAMPLE_VEHICLE } from '../utils/jobCardTemplates';
import { PAGES, getDefaultPermissions, DEFAULT_ROLE_PERMISSIONS, canManageUsers as canManageUsersUtil } from '../utils/rolePermissions';
import { useUserRole } from '../hooks/useUserRole';
import { useToast } from '../context/ToastContext';
import { listAuthUsers, sendPasswordReset } from '../services/firebaseFunctions';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth, currentCompanyName } from '../firebase/config';
import { configHelpers, companyConfig } from '../config/app';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import {
  ensureContrastOnBackground,
  mainContentBackgroundForTheme,
  normalizeHex,
  readableTextOnBackground,
  mutedSidebarTextOnBackground,
  defaultMainTextForTheme,
} from '../utils/colorContrast';
import {
  formatAuditLogDetails,
  formatAuditTime,
  formatEntityDisplay,
  getActionLabel,
} from '../utils/auditLogUtils';
import { ALL_COMPANY_STAGES, normalizeEnabledStages } from '../utils/enabledStages';

function parseHexFieldInput(raw) {
  const v = String(raw || '').trim();
  if (!v) return null;
  const withHash = v.startsWith('#') ? v : `#${v}`;
  if (/^#[0-9A-Fa-f]{3}$/i.test(withHash) || /^#[0-9A-Fa-f]{6}$/i.test(withHash)) {
    return normalizeHex(withHash);
  }
  return null;
}

const SettingsModal = ({ onClose }) => {
  const { garageInfo, updateGarageInfo } = useApp();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('crm');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('branding_primary_color') || '#33475b');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('branding_secondary_color') || '#f5f4f0');
  const [fontColor, setFontColor] = useState(localStorage.getItem('branding_font_color') || '');
  const fileInputRef = useRef(null);
  
  // User management state
  const [users, setUsers] = useState([]);
  const [authUsers, setAuthUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAuthUsers, setLoadingAuthUsers] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [assigningRole, setAssigningRole] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    roles: ['sales'], // Changed to array to support multiple roles
    username: '',
    permissions: {}
  });
  const [userError, setUserError] = useState('');
  const [showPermissions, setShowPermissions] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(null);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState('');

  // Company information state
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    address: '',
    contact: '',
    vatNumber: '',
    email: '',
    siteNames: ['Site A'],
    defaultTechnician: '',
    defaultSite: '',
    whatsappServiceDueRemindersEnabled: false,
    whatsappMotRemindersEnabled: false,
    enabledStages: [...ALL_COMPANY_STAGES],
  });
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState('');
  const [companySuccess, setCompanySuccess] = useState('');
  
  // Technicians management state (loaded from garageInfo, not users)
  const [technicians, setTechnicians] = useState([]);
  const [technicianError, setTechnicianError] = useState('');
  const [technicianSuccess, setTechnicianSuccess] = useState('');
  const [newTechnicianName, setNewTechnicianName] = useState('');

  // Role permissions management state
  const [rolePermissions, setRolePermissions] = useState({});
  const [loadingRolePermissions, setLoadingRolePermissions] = useState(false);
  const [rolePermissionsError, setRolePermissionsError] = useState('');
  const [rolePermissionsSuccess, setRolePermissionsSuccess] = useState('');

  // Temporary hex input states for color inputs
  const [tempPrimaryHex, setTempPrimaryHex] = useState(primaryColor);
  const [tempSecondaryHex, setTempSecondaryHex] = useState(secondaryColor);
  const [tempFontHex, setTempFontHex] = useState(fontColor || '');
  const [appearanceTheme, setAppearanceTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const [fontContrastNote, setFontContrastNote] = useState('');

  // WhatsApp templates state
  const [waTemplates, setWaTemplates] = useState([]);
  const [waTemplatesLoading, setWaTemplatesLoading] = useState(false);
  const [waTemplatesError, setWaTemplatesError] = useState('');

  // Audit log (optional view in Settings)
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  // Job card template (Templates tab)
  const [jobCardTemplateId, setJobCardTemplateId] = useState('classic');
  const [jobCardLabels, setJobCardLabels] = useState({ ...DEFAULT_JOB_CARD_LABELS });
  const [jobCardSaving, setJobCardSaving] = useState(false);
  const [jobCardSuccess, setJobCardSuccess] = useState('');

  const [themeSaveLoading, setThemeSaveLoading] = useState(false);
  const [themeSaveError, setThemeSaveError] = useState('');
  const [themeSaveSuccess, setThemeSaveSuccess] = useState('');

  const canViewWhatsAppTemplatesTab =
    (sessionStorage.getItem('userEmail') || '').toLowerCase().includes('plutoo.co.uk') ||
    (sessionStorage.getItem('username') || '').toLowerCase().includes('plutoo.co.uk');

  // Sync temp hex values when colors change externally
  useEffect(() => {
    setTempPrimaryHex(primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    setTempSecondaryHex(secondaryColor);
  }, [secondaryColor]);

  useEffect(() => {
    setTempFontHex(fontColor || '');
  }, [fontColor]);

  const brandPreview = useMemo(() => {
    const p = normalizeHex(primaryColor, '#33475b');
    const s = normalizeHex(secondaryColor, '#f5f4f0');
    const isDark = appearanceTheme === 'dark';
    const mainBg = mainContentBackgroundForTheme(isDark);
    const sidebarText = readableTextOnBackground(s);
    const sidebarMuted = mutedSidebarTextOnBackground(s);
    const onPrimaryBtn = readableTextOnBackground(p);
    let mainText;
    if (fontColor && String(fontColor).trim()) {
      mainText = ensureContrastOnBackground(normalizeHex(fontColor, '#1a1a1a'), mainBg, 4.5);
    } else {
      mainText = defaultMainTextForTheme(isDark);
    }
    return { p, s, mainBg, sidebarText, sidebarMuted, mainText, onPrimaryBtn, isDark };
  }, [primaryColor, secondaryColor, fontColor, appearanceTheme]);

  // Load users when users tab is active
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadAuthUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on tab switch only
  }, [activeTab]);

  // Load company information when company tab is active
  useEffect(() => {
    if (activeTab === 'company') {
      setCompanyForm({
        companyName: garageInfo?.companyName || '',
        address: garageInfo?.address || '',
        contact: garageInfo?.contact || '',
        vatNumber: garageInfo?.vatNumber || '',
        email: garageInfo?.email || '',
        siteNames: garageInfo?.siteNames && garageInfo.siteNames.length > 0 
          ? garageInfo.siteNames 
          : ['Site A'],
        defaultTechnician: garageInfo?.defaultTechnician || '',
        defaultSite: garageInfo?.defaultSite || '',
        whatsappServiceDueRemindersEnabled: garageInfo?.whatsappServiceDueRemindersEnabled === true,
        whatsappMotRemindersEnabled: garageInfo?.whatsappMotRemindersEnabled === true,
        enabledStages: normalizeEnabledStages(garageInfo?.enabledStages),
      });
      setNewTechnicianName('');
      loadTechnicians();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on tab switch only
  }, [activeTab, garageInfo]);

  // Load job card template when templates tab is active
  useEffect(() => {
    if (activeTab === 'templates') {
      const jc = garageInfo?.jobCardTemplate;
      if (jc && typeof jc === 'object') {
        setJobCardTemplateId(jc.templateId || 'classic');
        setJobCardLabels(prev => ({ ...DEFAULT_JOB_CARD_LABELS, ...(jc.customLabels || {}) }));
      } else {
        setJobCardTemplateId('classic');
        setJobCardLabels({ ...DEFAULT_JOB_CARD_LABELS });
      }
    }
  }, [activeTab, garageInfo?.jobCardTemplate]);

  // Load audit log when audit tab is active
  useEffect(() => {
    if (activeTab === 'audit') {
      setAuditLogLoading(true);
      auditLogService.getRecent(100).then((entries) => {
        setAuditLog(entries);
      }).catch(() => setAuditLog([])).finally(() => setAuditLogLoading(false));
    }
  }, [activeTab]);

  // Load WhatsApp templates when whatsapp-templates tab is active
  useEffect(() => {
    if (
      activeTab === 'whatsapp-templates' &&
      canViewWhatsAppTemplatesTab &&
      waTemplates.length === 0 &&
      !waTemplatesLoading
    ) {
      setWaTemplatesLoading(true);
      setWaTemplatesError('');
      const { firebaseConfig } = require('../config/app');
      const base = firebaseConfig.getFunctionsBaseUrl();
      const url = (process.env.REACT_APP_GET_WHATSAPP_TEMPLATES_URL || `${base}/getWhatsAppTemplates`);
      fetch(url)
        .then(r => r.json())
        .then(data => {
          if (data.error) throw new Error(data.error);
          setWaTemplates(data.templates || []);
        })
        .catch(err => setWaTemplatesError(err.message || 'Failed to load templates'))
        .finally(() => setWaTemplatesLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once when tab opens
  }, [activeTab, canViewWhatsAppTemplatesTab]);

  // Leave WhatsApp tab if user is not allowed to view it
  useEffect(() => {
    if (activeTab === 'whatsapp-templates' && !canViewWhatsAppTemplatesTab) {
      setActiveTab('crm');
    }
  }, [activeTab, canViewWhatsAppTemplatesTab]);

  // Load role permissions when roles tab is active
  useEffect(() => {
    if (activeTab === 'roles') {
      loadRolePermissions();
    }
  }, [activeTab]);

  // Merge duplicate users (same person, different docs/roles) into one row with combined roles
  const mergeUsersByIdentity = (usersList) => {
    const byKey = new Map(); // key = email || username || firebaseUid
    for (const u of usersList) {
      const key = (u.email && u.email.trim()) || (u.username && u.username.trim()) || (u.firebaseUid && u.firebaseUid.trim()) || u.id;
      const existing = byKey.get(key);
      const roles = u.roles && Array.isArray(u.roles) ? u.roles : (u.role ? [u.role] : ['sales']);
      if (!existing) {
        byKey.set(key, {
          ...u,
          roles: [...new Set(roles)],
          role: roles[0],
        });
      } else {
        const mergedRoles = [...new Set([...(existing.roles || (existing.role ? [existing.role] : [])), ...roles])];
        byKey.set(key, {
          ...existing,
          roles: mergedRoles,
          role: mergedRoles[0],
        });
      }
    }
    return Array.from(byKey.values());
  };

  // Load users from Firestore
  const loadUsers = async () => {
    setLoadingUsers(true);
    setUserError('');
    try {
      const usersList = await usersService.getAll();
      const merged = mergeUsersByIdentity(usersList);
      setUsers(merged);
    } catch (error) {
      console.error('Error loading users:', error);
      setUserError('Failed to load users. Please try again.');
    } finally {
      setLoadingUsers(false);
    }
  };

  // Load Firebase Auth users
  const loadAuthUsers = async () => {
    setLoadingAuthUsers(true);
    setUserError(''); // Clear previous errors
    try {
      const response = await listAuthUsers();
      setAuthUsers(response.users || []);
      setUserError(''); // Clear any previous errors on success
    } catch (error) {
      console.error('Error loading Firebase Auth users:', error);
      // Show the actual error message for better debugging
      const errorMessage = error.message || 'Failed to load Firebase Auth users. Please try again.';
      setUserError(errorMessage);
    } finally {
      setLoadingAuthUsers(false);
    }
  };

  // Assign role to Firebase Auth user
  const handleAssignRole = async (authUser, role) => {
    setAssigningRole(authUser.uid);
    setUserError('');
    try {
      // Check if user already exists in Firestore
      const existingUser = users.find(u => u.firebaseUid === authUser.uid || (u.email && authUser.email && u.email.toLowerCase() === authUser.email.toLowerCase()));
      
      if (existingUser) {
        // Update existing user
        await usersService.update(existingUser.id, {
          role: role,
          email: authUser.email || existingUser.email,
          firebaseUid: authUser.uid,
          updatedAt: new Date()
        });
      } else {
        // Create new user in Firestore directly (without creating Firebase Auth user)
        const username = authUser.email ? authUser.email.split('@')[0] : authUser.uid.substring(0, 8);
        const email = authUser.email || `${authUser.uid}@firebase.local`;
        
        // Auto-set admin role for admin email domain
        let userRole = role;
        if (configHelpers.isAdminEmail(email)) {
          userRole = 'admin';
        }
        
        const userData = {
          username: username,
          email: email,
          role: userRole,
          active: true,
          firebaseUid: authUser.uid,
          companyId: currentCompanyName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(doc(db, 'users', authUser.uid), userData, { merge: true });
      }
      
      // Reload users
      await loadUsers();
      setAssigningRole(null);
    } catch (error) {
      console.error('Error assigning role:', error);
      setUserError(`Failed to assign role: ${error.message}`);
      setAssigningRole(null);
    }
  };

  // Handle form input changes
  const handleUserFormChange = (field, value) => {
    setUserForm(prev => ({
      ...prev,
      [field]: value
    }));
    setUserError('');
  };

  // Reset user form
  const resetUserForm = () => {
    setUserForm({
      email: '',
      password: '',
      roles: ['sales'], // Changed to array
      username: '',
      permissions: {}
    });
    setEditingUser(null);
    setShowAddUser(false);
    setShowPermissions(false);
    setUserError('');
  };

  // Handle add user
  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');

    if (!userForm.email || !userForm.password) {
      setUserError('Email and password are required');
      return;
    }

    if (userForm.password.length < 6) {
      setUserError('Password must be at least 6 characters');
      return;
    }

    if (!userForm.roles || userForm.roles.length === 0) {
      setUserError('At least one role is required');
      return;
    }

    // Generate username from email if not provided
    const username = userForm.username || userForm.email.split('@')[0];

    // Auto-set admin role for admin email domain (god mode)
    let userRoles = [...userForm.roles];
    if (configHelpers.isAdminEmail(userForm.email) && !userRoles.includes('admin')) {
      userRoles.push('admin');
      console.log('Auto-adding admin role for admin email domain:', userForm.email);
    }

    try {
      console.log('Attempting to add user:', username, 'with email:', userForm.email, 'roles:', userRoles);
      console.log('Current auth state:', sessionStorage.getItem('authType'));
      
      // Save only custom permissions (overrides), including false values
      // This ensures that explicitly disabled pages are saved
      const customPermissions = Object.keys(userForm.permissions).length > 0 
        ? userForm.permissions 
        : undefined;
      
      console.log('Saving user with custom permissions:', customPermissions);
      
      const result = await usersService.add({
        username: username,
        password: userForm.password, // In production, this should be hashed
        roles: userRoles, // Use array of roles
        role: userRoles[0], // Keep primary role for backward compatibility
        email: userForm.email,
        active: true,
        permissions: customPermissions
      });
      console.log('User added successfully:', result);
      resetUserForm();
      loadUsers();
    } catch (error) {
      console.error('Error adding user - Full error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      setUserError(error.message || 'Failed to add user. Please try again.');
    }
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email || '',
      password: '', // Don't show password
      roles: user.roles || (user.role ? [user.role] : ['sales']), // Support both new and old format
      username: user.username || '',
      permissions: user.permissions || {}
    });
    setShowAddUser(true);
    setShowPermissions(false);
    setUserError('');
  };

  // Handle update user
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setUserError('');

    if (!userForm.email) {
      setUserError('Email is required');
      return;
    }

    if (!userForm.roles || userForm.roles.length === 0) {
      setUserError('At least one role is required');
      return;
    }

    try {
      // Generate username from email if not provided
      const username = userForm.username || userForm.email.split('@')[0];
      
      // Auto-add admin role for @plutoo.co.uk domain emails (god mode)
      let userRoles = [...userForm.roles];
      if (userForm.email && userForm.email.toLowerCase().endsWith('@plutoo.co.uk') && !userRoles.includes('admin')) {
        userRoles.push('admin');
        console.log('Auto-adding admin role for @plutoo.co.uk email:', userForm.email);
      }
      
      const updates = {
        username: username,
        roles: userRoles, // Use array of roles
        role: userRoles[0], // Keep primary role for backward compatibility
        email: userForm.email
      };

      // Only update password if it's provided
      if (userForm.password) {
        if (userForm.password.length < 6) {
          setUserError('Password must be at least 6 characters');
          return;
        }
        updates.password = userForm.password; // In production, this should be hashed
      }

      // Include permissions if they've been customized
      if (Object.keys(userForm.permissions).length > 0) {
        updates.permissions = userForm.permissions;
      }

      await usersService.update(editingUser.id, updates);
      resetUserForm();
      loadUsers();
      
      // Trigger role update event if current user was updated
      const currentUsername = sessionStorage.getItem('username');
      if (editingUser.username === currentUsername) {
        window.dispatchEvent(new Event('userRoleUpdated'));
      }
    } catch (error) {
      setUserError(error.message || 'Failed to update user. Please try again.');
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersService.delete(userId);
      loadUsers();
    } catch (error) {
      setUserError('Failed to delete user. Please try again.');
    }
  };

  // Handle password reset
  const handlePasswordReset = async (user) => {
    if (!user.email && !user.firebaseUid) {
      setUserError('User must have an email address or Firebase Auth account to reset password.');
      return;
    }

    if (!window.confirm(`Send password reset email to ${user.email || user.username}?`)) {
      return;
    }

    setResettingPassword(user.id);
    setUserError('');
    setPasswordResetSuccess(null);

    try {
      await sendPasswordReset(user.email, user.firebaseUid);
      setPasswordResetSuccess(user.id);
      setTimeout(() => {
        setPasswordResetSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Password reset error:', error);
      setUserError(error.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setResettingPassword(null);
    }
  };

  // Handle password change for current user
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess('');
    setPasswordChangeLoading(true);

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordChangeError('All fields are required');
      setPasswordChangeLoading(false);
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordChangeError('New password must be at least 6 characters long');
      setPasswordChangeLoading(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordChangeError('New passwords do not match');
      setPasswordChangeLoading(false);
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordChangeError('New password must be different from current password');
      setPasswordChangeLoading(false);
      return;
    }

    try {
      const authType = sessionStorage.getItem('authType');
      const username = sessionStorage.getItem('username');

      // Handle Firebase Auth users
      if (authType === 'firebase' && auth.currentUser) {
        const user = auth.currentUser;
        
        // Reauthenticate user with current password
        const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
        await reauthenticateWithCredential(user, credential);
        
        // Update password
        await updatePassword(user, passwordForm.newPassword);
        
        // Also update password in Firestore if user exists there
        try {
          const firestoreUser = await usersService.getByEmail(user.email);
          if (firestoreUser) {
            await usersService.update(firestoreUser.id, { password: passwordForm.newPassword });
          }
        } catch (firestoreError) {
          console.warn('Could not update password in Firestore:', firestoreError);
          // Continue - password is updated in Firebase Auth which is the primary auth
        }
        
        setPasswordChangeSuccess('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setPasswordChangeSuccess('');
        }, 5000);
      } 
      // Handle Firestore-only users
      else if (authType === 'firestore' || authType === 'admin') {
        // For Firestore users, we need to verify current password from Firestore
        let firestoreUser = null;
        if (username) {
          try {
            firestoreUser = await usersService.getByUsername(username);
          } catch (err) {
            console.error('Error fetching user:', err);
          }
        }
        
        if (!firestoreUser) {
          setPasswordChangeError('User not found. Please contact your administrator.');
          setPasswordChangeLoading(false);
          return;
        }

        // Verify current password
        if (firestoreUser.password !== passwordForm.currentPassword) {
          setPasswordChangeError('Current password is incorrect');
          setPasswordChangeLoading(false);
          return;
        }

        // Update password in Firestore
        await usersService.update(firestoreUser.id, { password: passwordForm.newPassword });
        
        setPasswordChangeSuccess('Password changed successfully!');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setPasswordChangeSuccess('');
        }, 5000);
      } else {
        setPasswordChangeError('Unable to change password. Please contact your administrator.');
      }
    } catch (error) {
      console.error('Password change error:', error);
      let errorMessage = 'Failed to change password. Please try again.';
      
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Current password is incorrect';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak. Please choose a stronger password.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'For security reasons, please log out and log back in before changing your password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Get current user's role and permissions
  const { role: currentUserRole, permissions: currentUserPermissions, loading: roleLoading } = useUserRole();
  const userEmail = sessionStorage.getItem('userEmail') || sessionStorage.getItem('username');

  // Helper to check if current user is admin (including admin email domain)
  const isCurrentUserAdmin = () => {
    return currentUserRole === 'admin' || configHelpers.isAdminEmail(userEmail);
  };

  // Helper to check if current user is admin or owner (for roles tab access)
  const canAccessRoles = () => {
    return currentUserRole === 'admin' || currentUserRole === 'owner' || configHelpers.isAdminEmail(userEmail);
  };

  // Check if user can manage users (owner or admin)
  const canManageUsers = () => {
    // Check for admin email domain first (always admin/god mode)
    if (configHelpers.isAdminEmail(userEmail)) {
      return true; // Admin email domain users are always admin (god mode)
    }
    
    // If role is still loading, check sessionStorage as fallback
    if (roleLoading || !currentUserRole) {
      const storedRole = sessionStorage.getItem('userRole');
      if (storedRole === 'admin' || storedRole === 'owner') {
        return true;
      }
      return false;
    }
    
    // Check using permission utility (admin and owner both have canManageUsers: true)
    const canManage = canManageUsersUtil(currentUserRole, currentUserPermissions);
    console.log('canManageUsers check - role:', currentUserRole, 'canManage:', canManage);
    return canManage;
  };

  // Handle permission toggle
  const handlePermissionToggle = (page) => {
    setUserForm(prev => {
      // Get current effective permission (custom or default)
      const currentEffective = prev.permissions[page] !== undefined 
        ? prev.permissions[page] === true
        : getDefaultPermissionForPage(page);
      
      // Toggle the effective permission
      const newValue = !currentEffective;
      
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [page]: newValue
        }
      };
    });
  };

  // Get page label
  const getPageLabel = (page) => {
    const labels = {
      [PAGES.OVERVIEW]: 'Dashboard',
      [PAGES.GARAGE_VEHICLES]: 'Garage Vehicles',
      [PAGES.GARAGE_REPORT]: 'Garage Report',
      [PAGES.REPORT]: 'Report',
      [PAGES.CALENDAR]: 'Calendar',
      [PAGES.INVOICES]: 'Invoices',
      [PAGES.ARCHIVE]: 'Archive',
      [PAGES.FINANCE]: 'Finance',
      [PAGES.SALES]: 'Sales',
      [PAGES.SALES_REPORT]: 'Sales Report',
      [PAGES.LINKS]: 'Links',
      [PAGES.PROMOTIONS]: 'Promotions',
      [PAGES.PAYMENT]: 'My Account',
      [PAGES.TECHNICIAN_DISPATCH]: 'Technicians',
      [PAGES.PARTS_LOOKUP]: 'Parts Lookup',
      [PAGES.TEST_ADMIN]: 'Test Admin'
    };
    return labels[page] || page;
  };

  // Get default permission for a page based on role
  const getDefaultPermissionForPage = (page) => {
    const defaultPerms = getDefaultPermissions(userForm.role);
    return defaultPerms[page] === true;
  };

  // Get effective permission (custom or default)
  const getEffectivePermission = (page) => {
    if (userForm.permissions[page] !== undefined) {
      return userForm.permissions[page] === true;
    }
    return getDefaultPermissionForPage(page);
  };

  // Load role permissions from Firestore
  const loadRolePermissions = async () => {
    setLoadingRolePermissions(true);
    setRolePermissionsError('');
    try {
      const savedPermissions = await rolePermissionsService.getDefaultRolePermissions();
      if (savedPermissions) {
        setRolePermissions(savedPermissions);
      } else {
        // Use default permissions from rolePermissions.js
        setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
      }
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setRolePermissionsError('Failed to load role permissions. Using defaults.');
      // Fallback to defaults
      setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
    } finally {
      setLoadingRolePermissions(false);
    }
  };

  // Handle role permission toggle
  const handleRolePermissionToggle = (role, page) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [page]: !(prev[role]?.[page] === true)
      }
    }));
  };

  // Save role permissions
  const handleSaveRolePermissions = async () => {
    setLoadingRolePermissions(true);
    setRolePermissionsError('');
    setRolePermissionsSuccess('');
    
    try {
      await rolePermissionsService.saveDefaultRolePermissions(rolePermissions);
      setRolePermissionsSuccess('Role permissions saved successfully!');
      setTimeout(() => setRolePermissionsSuccess(''), 3000);
      
      // Trigger a reload of user roles to apply new defaults
      window.dispatchEvent(new Event('userRoleUpdated'));
    } catch (error) {
      console.error('Error saving role permissions:', error);
      setRolePermissionsError('Failed to save role permissions. Please try again.');
    } finally {
      setLoadingRolePermissions(false);
    }
  };

  // Reset role permissions to defaults
  const handleResetRolePermissions = () => {
    if (window.confirm('Are you sure you want to reset all role permissions to defaults? This cannot be undone.')) {
      setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
      setRolePermissionsError('');
      setRolePermissionsSuccess('');
    }
  };

  // Load logo from Firebase Storage/Firestore on component mount
  useEffect(() => {
    const loadLogo = async () => {
      setLogoLoading(true);
      setLogoError('');
      
      // Set a timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setLogoLoading(false);
        console.warn('Logo loading timed out');
      }, 5000); // 5 second timeout
      
      try {
        // Load logo from Firestore only - no localStorage fallback
        const url = await logoStorageService.getLogoURL();
        clearTimeout(timeoutId);
        if (url) {
          setLogoUrl(url);
          console.log('Logo loaded successfully:', url);
        } else {
          console.log('No logo found in Firestore');
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('Error loading logo from Firestore:', error);
        // No fallback - logo will remain empty
      } finally {
        clearTimeout(timeoutId);
        setLogoLoading(false);
      }
    };
    loadLogo();
    
    // Listen for branding updates from other components
    const handleBrandingUpdate = (e) => {
      if (e.detail.logo !== undefined) {
        setLogoUrl(e.detail.logo || '');
      }
    };
    window.addEventListener('brandingUpdated', handleBrandingUpdate);
    
    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
    };
  }, []);

  // Theme form draft: sync from shared company settings (or local cache) when opening CRM tab / when branding loads
  useEffect(() => {
    if (activeTab !== 'crm') return;
    const b = garageInfo?.branding;
    if (b && typeof b === 'object') {
      setPrimaryColor(normalizeHex(b.primaryColor, '#33475b'));
      setSecondaryColor(normalizeHex(b.secondaryColor, '#f5f4f0'));
      setFontColor(b.fontColor ? normalizeHex(b.fontColor, '#1a1a1a') : '');
      setAppearanceTheme(b.appearanceTheme === 'dark' ? 'dark' : 'light');
    } else {
      setPrimaryColor(normalizeHex(localStorage.getItem('branding_primary_color') || '#33475b', '#33475b'));
      setSecondaryColor(normalizeHex(localStorage.getItem('branding_secondary_color') || '#f5f4f0', '#f5f4f0'));
      const fc = localStorage.getItem('branding_font_color');
      setFontColor(fc ? normalizeHex(fc, '#1a1a1a') : '');
      setAppearanceTheme(localStorage.getItem('theme') || 'light');
    }
  }, [activeTab, garageInfo?.branding]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      setLogoError('Please select an image file');
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('File size must be less than 2MB');
      return;
    }

    setLogoLoading(true);
    setLogoError('');
    
    try {
      // Upload to Firebase Storage - automatically saves to Firestore
      const downloadURL = await logoStorageService.uploadLogo(file);
      
      // Update state
      setLogoUrl(downloadURL);
      
      // Dispatch custom event to notify other components immediately
      window.dispatchEvent(new CustomEvent('brandingUpdated', {
        detail: { logo: downloadURL }
      }));
      
      console.log('Logo uploaded and saved successfully:', downloadURL);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setLogoError('Failed to upload logo. Please try again.');
      
      // If we reach here, uploadLogo failed, so we'll just show an error
      // The user can try uploading again
    } finally {
      setLogoLoading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = async () => {
    setLogoLoading(true);
    setLogoError('');
    
    try {
      // Delete from Firebase Storage and Firestore
      await logoStorageService.deleteLogo();
      
      // Update state
      setLogoUrl('');
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('brandingUpdated', {
        detail: { logo: '' }
      }));
      
      console.log('Logo removed successfully');
    } catch (error) {
      console.error('Error removing logo:', error);
      setLogoError('Failed to remove logo. Please try again.');
      // Still update UI even if deletion fails
      setLogoUrl('');
      window.dispatchEvent(new CustomEvent('brandingUpdated', {
        detail: { logo: '' }
      }));
    } finally {
      setLogoLoading(false);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePrimaryColorChange = (color) => {
    const hex = normalizeHex(color, primaryColor);
    setPrimaryColor(hex);
  };

  const handleSecondaryColorChange = (color) => {
    const hex = normalizeHex(color, secondaryColor);
    setSecondaryColor(hex);
  };

  const handleFontColorChange = (color) => {
    const mainBg = mainContentBackgroundForTheme(appearanceTheme === 'dark');
    if (color) {
      const hex = normalizeHex(color, '#1a1a1a');
      setFontColor(hex);
      const safe = ensureContrastOnBackground(hex, mainBg, 4.5);
      setFontContrastNote(safe.toLowerCase() !== hex.toLowerCase() ? 'Main text color was adjusted slightly so it stays readable on the page background.' : '');
    } else {
      setFontColor('');
      setFontContrastNote('');
    }
  };

  const handleAppearanceThemeChange = (mode) => {
    setAppearanceTheme(mode);
    if (fontColor) {
      const mainBg = mainContentBackgroundForTheme(mode === 'dark');
      const safe = ensureContrastOnBackground(fontColor, mainBg, 4.5);
      setFontContrastNote(safe.toLowerCase() !== fontColor.toLowerCase() ? 'Main text color was adjusted slightly so it stays readable on the page background.' : '');
    }
  };

  const handleSaveTheme = async () => {
    setThemeSaveLoading(true);
    setThemeSaveError('');
    setThemeSaveSuccess('');
    const loadingId = toast.loading('Saving theme…');
    try {
      const branding = {
        primaryColor: normalizeHex(primaryColor, '#33475b'),
        secondaryColor: normalizeHex(secondaryColor, '#f5f4f0'),
        fontColor: fontColor ? normalizeHex(fontColor, '#1a1a1a') : '',
        appearanceTheme,
      };
      await updateGarageInfo({ branding });
      setThemeSaveSuccess('Saved. Everyone on the platform will use this theme.');
      toast.update(loadingId, 'Theme saved', 'success');
      setTimeout(() => setThemeSaveSuccess(''), 6000);
    } catch (e) {
      console.error(e);
      setThemeSaveError(e?.message || 'Could not save theme. Try again.');
      toast.update(loadingId, e?.message || 'Could not save theme', 'error');
    } finally {
      setThemeSaveLoading(false);
    }
  };

  const resetToDefaults = () => {
    const navyColor = '#001F3F';
    const lighterBlue = '#4A90E2';
    const p = normalizeHex(lighterBlue, '#4A90E2');
    const s = normalizeHex(navyColor, '#001F3F');
    setPrimaryColor(p);
    setSecondaryColor(s);
    setFontColor('');
    setFontContrastNote('');
    setTempPrimaryHex(p);
    setTempSecondaryHex(s);
    setTempFontHex('');
  };

  // Load technicians from garageInfo (stored in company settings, not as users)
  const loadTechnicians = () => {
    const list = garageInfo?.technicians && Array.isArray(garageInfo.technicians)
      ? [...garageInfo.technicians]
      : [];
    list.sort((a, b) => (a || '').toLowerCase().localeCompare((b || '').toLowerCase()));
    setTechnicians(list);
  };

  // Handle adding a technician (adds to garageInfo, no user account created)
  const handleAddTechnician = async (technicianName) => {
    if (!technicianName || !technicianName.trim()) {
      setTechnicianError('Technician name is required');
      return;
    }

    const trimmedName = technicianName.trim();
    const existingList = garageInfo?.technicians || [];
    const exists = existingList.some(
      (name) => (name || '').toLowerCase() === trimmedName.toLowerCase()
    );
    if (exists) {
      setTechnicianError('Technician already exists');
      return;
    }

    setTechnicianError('');
    setTechnicianSuccess('');

    try {
      const updatedTechnicians = [...existingList, trimmedName];
      await updateGarageInfo({ technicians: updatedTechnicians });
      setTechnicianSuccess('Technician added successfully!');
      setTimeout(() => setTechnicianSuccess(''), 3000);
      loadTechnicians();
    } catch (error) {
      console.error('Error adding technician:', error);
      setTechnicianError(`Failed to add technician: ${error.message}`);
    }
  };

  // Handle removing a technician (removes from garageInfo list)
  const handleRemoveTechnician = async (technicianName) => {
    if (!technicianName) {
      setTechnicianError('Invalid technician');
      return;
    }

    setTechnicianError('');
    setTechnicianSuccess('');

    try {
      const existingList = garageInfo?.technicians || [];
      const updatedTechnicians = existingList.filter(
        (name) => (name || '').toLowerCase() !== technicianName.toLowerCase()
      );
      await updateGarageInfo({ technicians: updatedTechnicians });
      setTechnicianSuccess('Technician removed successfully!');
      setTimeout(() => setTechnicianSuccess(''), 3000);
      loadTechnicians();
    } catch (error) {
      console.error('Error removing technician:', error);
      setTechnicianError(`Failed to remove technician: ${error.message}`);
    }
  };

  // Handle company form input changes
  const handleCompanyFormChange = (field, value) => {
    setCompanyForm(prev => ({
      ...prev,
      [field]: value
    }));
    setCompanyError('');
    setCompanySuccess('');
  };

  // Handle company information save
  const handleSaveCompanyInfo = async (e) => {
    e.preventDefault();
    setCompanyLoading(true);
    setCompanyError('');
    setCompanySuccess('');
    const loadingId = toast.loading('Saving company settings…');

    try {
      await updateGarageInfo({
        companyName: companyForm.companyName,
        address: companyForm.address,
        contact: companyForm.contact,
        vatNumber: companyForm.vatNumber,
        email: companyForm.email,
        siteNames: companyForm.siteNames,
        defaultTechnician: companyForm.defaultTechnician || '',
        defaultSite: companyForm.defaultSite || '',
        whatsappServiceDueRemindersEnabled: companyForm.whatsappServiceDueRemindersEnabled === true,
        whatsappMotRemindersEnabled: companyForm.whatsappMotRemindersEnabled === true,
        enabledStages: normalizeEnabledStages(companyForm.enabledStages),
      });
      setCompanySuccess('Company information saved successfully!');
      toast.update(loadingId, 'Company settings saved', 'success');
      setTimeout(() => setCompanySuccess(''), 3000);
    } catch (error) {
      console.error('Error saving company information:', error);
      setCompanyError('Failed to save company information. Please try again.');
      toast.update(loadingId, 'Failed to save company settings', 'error');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleSaveJobCardTemplate = async (e) => {
    e.preventDefault();
    setJobCardSaving(true);
    setJobCardSuccess('');
    try {
      await updateGarageInfo({
        jobCardTemplate: {
          templateId: jobCardTemplateId,
          customLabels: jobCardLabels
        }
      });
      setJobCardSuccess('Job card template saved.');
      setTimeout(() => setJobCardSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving job card template:', err);
    } finally {
      setJobCardSaving(false);
    }
  };

  const handleOpenJobCardPreview = () => {
    const html = generateJobCardHTML(SAMPLE_VEHICLE, { templateId: jobCardTemplateId, customLabels: jobCardLabels });
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="settings-modal is-open" onClick={(e) => {
      if (e.target.classList.contains('settings-modal')) {
        onClose();
      }
    }}>
      <div className="settings-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <div className="settings-header-content">
            <h2 className="settings-title">Settings</h2>
            <p className="settings-subtitle">Customize your application preferences</p>
          </div>
          <button 
            type="button" 
            className="settings-close" 
            onClick={onClose}
            aria-label="Close settings"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 5L5 15M5 5l10 10"/>
            </svg>
          </button>
        </div>
        
        <div className="settings-tabs-container">
          <div className="settings-tabs">
            <button
              type="button"
              className={`settings-tab ${activeTab === 'crm' ? 'active' : ''}`}
              onClick={() => setActiveTab('crm')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
                  <path d="M12 2.69v8.31"></path>
                </svg>
              </span>
              <span>Branding</span>
            </button>
            {isCurrentUserAdmin() && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <span className="settings-tab-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </span>
                <span>Users</span>
              </button>
            )}
            <button
              type="button"
              className={`settings-tab ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 21h18"></path>
                  <path d="M5 21V7l8-4v18"></path>
                  <path d="M19 21V11l-6-4"></path>
                  <rect x="9" y="9" width="4" height="4"></rect>
                  <rect x="9" y="17" width="4" height="4"></rect>
                  <rect x="13" y="13" width="4" height="4"></rect>
                </svg>
              </span>
              <span>Company</span>
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'templates' ? 'active' : ''}`}
              onClick={() => setActiveTab('templates')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </span>
              <span>Templates</span>
            </button>
            {canAccessRoles() && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'roles' ? 'active' : ''}`}
                onClick={() => setActiveTab('roles')}
              >
                <span className="settings-tab-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                </span>
                <span>Roles</span>
              </button>
            )}
            <button
              type="button"
              className={`settings-tab ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <line x1="10" y1="9" x2="8" y2="9"></line>
                </svg>
              </span>
              <span>Audit Log</span>
            </button>
            <button
              type="button"
              className={`settings-tab ${activeTab === 'account' ? 'active' : ''}`}
              onClick={() => setActiveTab('account')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </span>
              <span>Account</span>
            </button>
            {canViewWhatsAppTemplatesTab && (
              <button
                type="button"
                className={`settings-tab ${activeTab === 'whatsapp-templates' ? 'active' : ''}`}
                onClick={() => setActiveTab('whatsapp-templates')}
              >
                <span className="settings-tab-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </span>
                <span>WhatsApp Templates</span>
              </button>
            )}
            <button
              type="button"
              className={`settings-tab ${activeTab === 'help' ? 'active' : ''}`}
              onClick={() => setActiveTab('help')}
            >
              <span className="settings-tab-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </span>
              <span>Help</span>
            </button>
          </div>
        </div>

        <div className="settings-body">
          {activeTab === 'crm' && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Company Logo</h3>
                  <p className="settings-section-subtitle">Upload your logo to customize the interface</p>
                </div>
                
                <div className="settings-form-group">
                  {logoError && (
                    <div style={{
                      padding: '12px',
                      marginBottom: '16px',
                      borderRadius: '8px',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      color: '#dc2626',
                      fontSize: '13px'
                    }}>
                      {logoError}
                    </div>
                  )}
                  <div className="logo-upload-container">
                    {logoLoading ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px',
                        color: 'var(--text-muted)'
                      }}>
                        <svg 
                          width="24" 
                          height="24" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2"
                          style={{
                            animation: 'spin 1s linear infinite',
                            marginBottom: '12px'
                          }}
                        >
                          <circle cx="12" cy="12" r="10" opacity="0.25"/>
                          <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75"/>
                        </svg>
                        <p>Uploading logo...</p>
                      </div>
                    ) : logoUrl ? (
                      <div className="logo-preview-container">
                        <img 
                          src={logoUrl} 
                          alt="Company Logo" 
                          className="logo-preview"
                          style={{ maxWidth: '300px', maxHeight: '200px' }}
                        />
                        <div className="logo-actions">
                          <button 
                            type="button" 
                            className="logo-change-btn"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={logoLoading}
                          >
                            Change Logo
                          </button>
                          <button 
                            type="button" 
                            className="logo-remove-btn"
                            onClick={handleRemoveLogo}
                            disabled={logoLoading}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="logo-upload-area">
                        <div 
                          className="logo-upload-placeholder"
                          onClick={() => fileInputRef.current?.click()}
                          style={{ cursor: 'pointer' }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="9" cy="9" r="2"/>
                            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                          </svg>
                          <p>Click to upload your logo</p>
                          <p className="upload-hint">PNG, JPG up to 2MB</p>
                        </div>
                        <button 
                          type="button" 
                          className="logo-upload-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={logoLoading}
                        >
                          Upload Logo
                        </button>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      style={{ display: 'none' }}
                      disabled={logoLoading}
                    />
                  </div>
                </div>

              </div>

              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Theme &amp; colours</h3>
                  <p className="settings-section-subtitle">
                    One shared look for every user. Adjust options below, then click <strong>Save theme</strong> to apply for the whole platform. The live preview updates as you change colours; the rest of the app updates after you save.
                  </p>
                </div>

                {themeSaveError ? (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#dc2626',
                    fontSize: '13px'
                  }}>
                    {themeSaveError}
                  </div>
                ) : null}
                {themeSaveSuccess ? (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid rgba(16, 185, 129, 0.35)',
                    color: '#047857',
                    fontSize: '13px'
                  }}>
                    {themeSaveSuccess}
                  </div>
                ) : null}

                <div className="settings-form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)' }}>
                    Live preview
                  </label>
                  <div
                    className="branding-live-preview"
                    style={{
                      display: 'flex',
                      borderRadius: 12,
                      overflow: 'hidden',
                      border: '1px solid var(--border)',
                      minHeight: 112,
                      boxShadow: 'var(--shadow)',
                    }}
                  >
                    <div
                      style={{
                        width: 108,
                        flexShrink: 0,
                        background: brandPreview.s,
                        color: brandPreview.sidebarText,
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        fontSize: 12,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>Menu</span>
                      <span style={{ color: brandPreview.sidebarMuted, fontSize: 11 }}>Dashboard</span>
                      <span style={{ color: brandPreview.sidebarMuted, fontSize: 11 }}>Vehicles</span>
                      <div style={{ marginTop: 'auto' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '5px 8px',
                            borderRadius: 6,
                            background: brandPreview.p,
                            color: brandPreview.onPrimaryBtn,
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                        >
                          Active
                        </span>
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: brandPreview.mainBg,
                        color: brandPreview.mainText,
                        padding: 12,
                        fontSize: 13,
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>Page preview</div>
                      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, opacity: 0.95 }}>
                        Body text and tables follow the main text colour. Save to apply this theme for all users.
                      </p>
                      <button
                        type="button"
                        style={{
                          marginTop: 10,
                          padding: '7px 14px',
                          borderRadius: 8,
                          border: 'none',
                          background: brandPreview.p,
                          color: brandPreview.onPrimaryBtn,
                          cursor: 'default',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Primary button
                      </button>
                    </div>
                  </div>
                </div>

                <div className="settings-form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)' }}>
                    Appearance
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className={appearanceTheme === 'light' ? 'primary-btn' : 'logo-change-btn'}
                      onClick={() => handleAppearanceThemeChange('light')}
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                    >
                      Light
                    </button>
                    <button
                      type="button"
                      className={appearanceTheme === 'dark' ? 'primary-btn' : 'logo-change-btn'}
                      onClick={() => handleAppearanceThemeChange('dark')}
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                    >
                      Dark
                    </button>
                  </div>
                </div>

                <div className="settings-form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)' }}>
                    Primary accent
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="color"
                      className="settings-color-input"
                      value={normalizeHex(primaryColor, '#33475b')}
                      onInput={(e) => handlePrimaryColorChange(e.target.value)}
                      onChange={(e) => handlePrimaryColorChange(e.target.value)}
                      aria-label="Primary colour"
                      style={{ width: 48, height: 36, padding: 0, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={tempPrimaryHex}
                      onChange={(e) => setTempPrimaryHex(e.target.value)}
                      onBlur={() => {
                        const parsed = parseHexFieldInput(tempPrimaryHex);
                        if (parsed) handlePrimaryColorChange(parsed);
                        else setTempPrimaryHex(primaryColor);
                      }}
                      placeholder="#4A90E2"
                      style={{
                        flex: '1 1 140px',
                        minWidth: 120,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--border)',
                        fontSize: '13px',
                        color: 'var(--text-dark)',
                        background: 'var(--card-bg, #fff)',
                      }}
                    />
                  </div>
                </div>

                <div className="settings-form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)' }}>
                    Sidebar &amp; header background
                  </label>
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Menu labels pick white or dark automatically so they stay visible on this colour.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="color"
                      className="settings-color-input"
                      value={normalizeHex(secondaryColor, '#f5f4f0')}
                      onInput={(e) => handleSecondaryColorChange(e.target.value)}
                      onChange={(e) => handleSecondaryColorChange(e.target.value)}
                      aria-label="Secondary colour"
                      style={{ width: 48, height: 36, padding: 0, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={tempSecondaryHex}
                      onChange={(e) => setTempSecondaryHex(e.target.value)}
                      onBlur={() => {
                        const parsed = parseHexFieldInput(tempSecondaryHex);
                        if (parsed) handleSecondaryColorChange(parsed);
                        else setTempSecondaryHex(secondaryColor);
                      }}
                      placeholder="#001F3F"
                      style={{
                        flex: '1 1 140px',
                        minWidth: 120,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--border)',
                        fontSize: '13px',
                        color: 'var(--text-dark)',
                        background: 'var(--card-bg, #fff)',
                      }}
                    />
                  </div>
                </div>

                <div className="settings-form-group">
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)' }}>
                    Main text colour (optional)
                  </label>
                  <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Leave cleared to use the default for light or dark mode. If a chosen colour is too faint on the page background, it is nudged for readability.
                  </p>
                  {fontContrastNote ? (
                    <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--primary)' }}>{fontContrastNote}</p>
                  ) : null}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="color"
                      className="settings-color-input"
                      value={normalizeHex(
                        fontColor || defaultMainTextForTheme(appearanceTheme === 'dark'),
                        defaultMainTextForTheme(appearanceTheme === 'dark')
                      )}
                      onInput={(e) => handleFontColorChange(e.target.value)}
                      onChange={(e) => handleFontColorChange(e.target.value)}
                      aria-label="Main text colour"
                      style={{ width: 48, height: 36, padding: 0, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={tempFontHex}
                      onChange={(e) => setTempFontHex(e.target.value)}
                      onBlur={() => {
                        const v = tempFontHex.trim();
                        if (v === '') {
                          handleFontColorChange('');
                          return;
                        }
                        const parsed = parseHexFieldInput(tempFontHex);
                        if (parsed) handleFontColorChange(parsed);
                        else setTempFontHex(fontColor || '');
                      }}
                      placeholder="Default (theme)"
                      style={{
                        flex: '1 1 140px',
                        minWidth: 120,
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid var(--border)',
                        fontSize: '13px',
                        color: 'var(--text-dark)',
                        background: 'var(--card-bg, #fff)',
                      }}
                    />
                    <button
                      type="button"
                      className="logo-change-btn"
                      onClick={() => handleFontColorChange('')}
                      style={{ padding: '8px 12px', fontSize: '13px' }}
                    >
                      Use default
                    </button>
                  </div>
                </div>

                <div className="settings-form-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="primary-btn"
                    onClick={handleSaveTheme}
                    disabled={themeSaveLoading}
                    style={{ padding: '10px 20px', fontSize: '14px', fontWeight: 600 }}
                  >
                    {themeSaveLoading ? 'Saving…' : 'Save theme'}
                  </button>
                  <button type="button" className="logo-upload-btn" onClick={resetToDefaults} disabled={themeSaveLoading}>
                    Reset colours to defaults
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (currentUserRole === 'admin' || (userEmail && userEmail.toLowerCase().endsWith('@plutoo.co.uk'))) && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                {userError && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '20px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#dc2626',
                    fontSize: '13px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {userError}
                  </div>
                )}

                <div className="settings-section-header">
                  <div>
                    <h3 className="settings-section-title">User Management</h3>
                    <p className="settings-section-subtitle">Manage users and permissions</p>
                  </div>
                  {canManageUsers() && (
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={() => {
                        resetUserForm();
                        setShowAddUser(true);
                      }}
                      style={{ padding: '8px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}
                    >
                      + Add User
                    </button>
                  )}
                </div>

                {showAddUser && canManageUsers() && (
                  <div style={{
                    padding: '20px',
                    marginBottom: '24px',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                    background: 'var(--light-bg)'
                  }}>
                    <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: 600, color: 'var(--text-dark)' }}>
                      {editingUser ? 'Edit User' : 'Add New User'}
                    </h4>
                    <form onSubmit={editingUser ? handleUpdateUser : handleAddUser}>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '16px', 
                        marginBottom: '16px' 
                      }}>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px', 
                            fontSize: '13px', 
                            fontWeight: 600, 
                            color: 'var(--text-dark)' 
                          }}>
                            Email *
                          </label>
                          <input
                            type="email"
                            className="settings-input"
                            value={userForm.email}
                            onChange={(e) => handleUserFormChange('email', e.target.value)}
                            required
                            placeholder="user@example.com"
                          />
                          <p style={{ 
                            margin: '4px 0 0 0', 
                            fontSize: '11px', 
                            color: 'var(--text-muted)' 
                          }}>
                            Used for Firebase Authentication login
                          </p>
                        </div>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px', 
                            fontSize: '13px', 
                            fontWeight: 600, 
                            color: 'var(--text-dark)' 
                          }}>
                            Roles * (select multiple)
                          </label>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: '8px',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            background: 'var(--light-bg)'
                          }}>
                            {['sales', 'technician', 'owner'].map(role => {
                              const roleColors = {
                                owner: '#dc2626',
                                sales: '#10b981',
                                technician: '#3b82f6'
                              };
                              const isSelected = userForm.roles.includes(role);
                              const isDisabled = configHelpers.isAdminEmail(userForm.email);
                              
                              return (
                                <label
                                  key={role}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    background: isSelected ? `${roleColors[role]}20` : 'transparent',
                                    border: `1px solid ${isSelected ? roleColors[role] : 'var(--border)'}`,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    opacity: isDisabled ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={(e) => {
                                    if (!isDisabled) {
                                      e.preventDefault();
                                      const newRoles = isSelected
                                        ? userForm.roles.filter(r => r !== role)
                                        : [...userForm.roles, role];
                                      handleUserFormChange('roles', newRoles);
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onChange={() => {}} // Handled by label onClick
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      cursor: isDisabled ? 'not-allowed' : 'pointer'
                                    }}
                                  />
                                  <span style={{
                                    fontSize: '13px',
                                    color: isSelected ? roleColors[role] : 'var(--text-dark)',
                                    fontWeight: isSelected ? 600 : 400,
                                    textTransform: 'capitalize'
                                  }}>
                                    {role}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                          {configHelpers.isAdminEmail(userForm.email) && (
                            <p style={{ 
                              margin: '8px 0 0 0', 
                              fontSize: '11px', 
                              color: '#ea580c',
                              fontWeight: 500
                            }}>
                              ⚡ {companyConfig.adminEmailDomain} emails automatically get Admin role (God Mode)
                            </p>
                          )}
                          <p style={{ 
                            margin: '4px 0 0 0', 
                            fontSize: '11px', 
                            color: 'var(--text-muted)' 
                          }}>
                            Select one or more roles for this user
                          </p>
                        </div>
                      </div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '16px', 
                        marginBottom: '20px' 
                      }}>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px', 
                            fontSize: '13px', 
                            fontWeight: 600, 
                            color: 'var(--text-dark)' 
                          }}>
                            {editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}
                          </label>
                          <input
                            type="password"
                            className="settings-input"
                            value={userForm.password}
                            onChange={(e) => handleUserFormChange('password', e.target.value)}
                            required={!editingUser}
                            minLength={6}
                          />
                        </div>
                        <div>
                          <label style={{ 
                            display: 'block', 
                            marginBottom: '8px', 
                            fontSize: '13px', 
                            fontWeight: 600, 
                            color: 'var(--text-dark)' 
                          }}>
                            Username (optional)
                          </label>
                          <input
                            type="text"
                            className="settings-input"
                            value={userForm.username}
                            onChange={(e) => handleUserFormChange('username', e.target.value)}
                            placeholder="Display name or username"
                          />
                          <p style={{ 
                            margin: '4px 0 0 0', 
                            fontSize: '11px', 
                            color: 'var(--text-muted)' 
                          }}>
                            {userForm.username 
                              ? 'Can be used for login instead of email' 
                              : 'If not provided, email will be used as username'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Page Access Permissions Section */}
                      <div style={{
                        marginTop: '24px',
                        padding: '16px',
                        borderRadius: '8px',
                        background: 'var(--light-bg)',
                        border: '1px solid var(--border)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px'
                        }}>
                          <div>
                            <h5 style={{ 
                              margin: 0, 
                              fontSize: '14px', 
                              fontWeight: 600, 
                              color: 'var(--text-dark)' 
                            }}>
                              Page Access Permissions
                            </h5>
                            <p style={{ 
                              margin: '4px 0 0 0', 
                              fontSize: '12px', 
                              color: 'var(--text-muted)' 
                            }}>
                              Customize which pages this user can access
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowPermissions(!showPermissions)}
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              background: 'transparent',
                              border: '1px solid var(--border)',
                              borderRadius: '6px',
                              color: 'var(--text-dark)',
                              cursor: 'pointer'
                            }}
                          >
                            {showPermissions ? 'Hide' : 'Show'} Permissions
                          </button>
                        </div>

                        {showPermissions && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '12px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            padding: '8px'
                          }}>
                            {Object.values(PAGES).map(page => {
                              const hasAccess = getEffectivePermission(page);
                              const isCustom = userForm.permissions[page] !== undefined;
                              return (
                                <label
                                  key={page}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    background: hasAccess ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    border: `1px solid ${hasAccess ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                    cursor: 'pointer',
                                    position: 'relative'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={hasAccess}
                                    onChange={() => handlePermissionToggle(page)}
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                  <span style={{
                                    fontSize: '13px',
                                    color: 'var(--text-dark)',
                                    flex: 1
                                  }}>
                                    {getPageLabel(page)}
                                  </span>
                                  {isCustom && (
                                    <span style={{
                                      fontSize: '10px',
                                      color: 'var(--primary)',
                                      fontWeight: 600
                                    }}>
                                      Custom
                                    </span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div style={{ 
                        display: 'flex', 
                        gap: '12px', 
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        marginTop: '20px'
                      }}>
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={resetUserForm}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="primary-btn"
                        >
                          {editingUser ? 'Update User' : 'Add User'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {!canManageUsers() && (
                  <div style={{
                    padding: '20px',
                    marginBottom: '24px',
                    borderRadius: '12px',
                    background: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    color: '#92400e'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4M10 14h.01"/>
                      </svg>
                      <div>
                        <strong>Limited Access</strong>
                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', opacity: 0.9 }}>
                          Only owners can add, edit, or remove users. Contact your owner for user management.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {loadingUsers ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <div style={{ 
                      display: 'inline-block',
                      width: '24px',
                      height: '24px',
                      border: '3px solid var(--border)',
                      borderTopColor: 'var(--primary)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: '12px'
                    }}></div>
                    <p style={{ margin: 0, fontSize: '14px' }}>Loading users...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <svg 
                      width="48" 
                      height="48" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ opacity: 0.5, marginBottom: '16px' }}
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      No users found. {canManageUsers() && 'Click "Add User" to create one.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table View */}
                    <div className="users-table-container" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                      <div className="users-table-wrapper">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th>Username</th>
                              <th>Role</th>
                              <th>Email</th>
                              <th>Status</th>
                              {isCurrentUserAdmin() && <th>Auth Status</th>}
                              {canManageUsers() && <th>Actions</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user) => {
                              const roleColors = {
                                owner: '#dc2626',
                                admin: '#ea580c',
                                manager: '#2563eb',
                                sales: '#10b981',
                                technician: '#3b82f6'
                              };
                              const isCurrentUser = sessionStorage.getItem('username') === user.username;
                              return (
                                <tr key={user.id}>
                                  <td className="user-name-cell" style={{ fontWeight: isCurrentUser ? 600 : 500 }}>
                                    {user.username}
                                    {isCurrentUser && (
                                      <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--primary)', fontWeight: 500 }}>
                                        (You)
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                      {(user.roles || (user.role ? [user.role] : ['sales'])).map((role, idx) => (
                                        <span
                                          key={idx}
                                          className="role-pill"
                                          style={{
                                            background: `${roleColors[role] || '#6b7280'}20`,
                                            color: roleColors[role] || '#6b7280'
                                          }}
                                        >
                                          {role}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="user-email-cell">
                                    {user.email || <span style={{ fontStyle: 'italic' }}>–</span>}
                                  </td>
                                  <td>
                                    <span
                                      className="status-pill"
                                      style={{
                                        background: user.active !== false ? '#10b98120' : '#6b728020',
                                        color: user.active !== false ? '#10b981' : '#6b7280'
                                      }}
                                    >
                                      {user.active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  {isCurrentUserAdmin() && (
                                    <td>
                                      {user.firebaseUid ? (
                                        <span className="auth-pill" style={{ background: '#3b82f620', color: '#3b82f6' }}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                          </svg>
                                          Firebase Auth
                                        </span>
                                      ) : (
                                        <span className="auth-pill" style={{ background: '#6b728020', color: '#6b7280' }}>
                                          Firestore Only
                                        </span>
                                      )}
                                    </td>
                                  )}
                                  {canManageUsers() && (
                                    <td>
                                      <div className="user-actions">
                                        <button
                                          type="button"
                                          className="secondary-btn"
                                          onClick={() => handleEditUser(user)}
                                        >
                                          Edit
                                        </button>
                                        {(user.email || user.firebaseUid) && (
                                          <button
                                            type="button"
                                            className="secondary-btn"
                                            onClick={() => handlePasswordReset(user)}
                                            disabled={resettingPassword === user.id}
                                            style={{
                                              borderColor: 'rgba(59, 130, 246, 0.3)',
                                              color: passwordResetSuccess === user.id ? '#10b981' : '#3b82f6',
                                              opacity: resettingPassword === user.id ? 0.6 : 1,
                                              cursor: resettingPassword === user.id ? 'not-allowed' : 'pointer'
                                            }}
                                          >
                                            {resettingPassword === user.id ? 'Sending...' : passwordResetSuccess === user.id ? 'Sent ✓' : 'Reset Password'}
                                          </button>
                                        )}
                                        {!isCurrentUser && (
                                          <button
                                            type="button"
                                            className="secondary-btn"
                                            onClick={() => handleDeleteUser(user.id)}
                                            style={{ borderColor: 'rgba(239, 68, 68, 0.3)', color: '#dc2626' }}
                                          >
                                            Delete
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* User cards (primary view) */}
                    <div className="users-card-container">
                      {users.map((user) => {
                        const roleColors = {
                          owner: '#dc2626',
                          admin: '#ea580c',
                          manager: '#2563eb',
                          sales: '#10b981',
                          technician: '#3b82f6'
                        };
                        const isCurrentUser = sessionStorage.getItem('username') === user.username;
                        return (
                          <div key={user.id} className="user-card">
                            <div className="user-card-header">
                              <div className="user-card-info">
                                <div className="user-card-name">
                                  {user.username}
                                  {isCurrentUser && (
                                    <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--primary)', fontWeight: 500 }}>
                                      (You)
                                    </span>
                                  )}
                                </div>
                                <div className="user-card-email">
                                  {user.email || <span style={{ fontStyle: 'italic' }}>No email</span>}
                                </div>
                              </div>
                              <div className="user-card-badges">
                                {(user.roles || (user.role ? [user.role] : ['sales'])).map((role, idx) => (
                                  <span
                                    key={idx}
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: '8px',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      background: `${roleColors[role] || '#6b7280'}20`,
                                      color: roleColors[role] || '#6b7280',
                                      textTransform: 'capitalize',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {role}
                                  </span>
                                ))}
                                <span
                                  style={{
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    background: user.active !== false ? '#10b98120' : '#6b728020',
                                    color: user.active !== false ? '#10b981' : '#6b7280',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {user.active !== false ? 'Active' : 'Inactive'}
                                </span>
                                {isCurrentUserAdmin() && (
                                  <>
                                    {user.firebaseUid ? (
                                      <span
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          padding: '4px 10px',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          fontWeight: 500,
                                          background: '#3b82f620',
                                          color: '#3b82f6',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                          <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                        Auth
                                      </span>
                                    ) : (
                                      <span
                                        style={{
                                          padding: '4px 10px',
                                          borderRadius: '8px',
                                          fontSize: '12px',
                                          fontWeight: 500,
                                          background: '#6b728020',
                                          color: '#6b7280',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        Firestore
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {canManageUsers() && (
                              <div className="user-card-actions">
                                <button type="button" className="secondary-btn" onClick={() => handleEditUser(user)}>
                                  Edit
                                </button>
                                {(user.email || user.firebaseUid) && (
                                  <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={() => handlePasswordReset(user)}
                                    disabled={resettingPassword === user.id}
                                    style={{
                                      borderColor: 'rgba(59, 130, 246, 0.3)',
                                      color: passwordResetSuccess === user.id ? '#10b981' : '#3b82f6',
                                      opacity: resettingPassword === user.id ? 0.6 : 1,
                                      cursor: resettingPassword === user.id ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    {resettingPassword === user.id ? 'Sending...' : passwordResetSuccess === user.id ? 'Sent ✓' : 'Reset Password'}
                                  </button>
                                )}
                                {!isCurrentUser && (
                                  <button
                                    type="button"
                                    className="secondary-btn"
                                    onClick={() => handleDeleteUser(user.id)}
                                    style={{ borderColor: 'rgba(239, 68, 68, 0.3)', color: '#dc2626' }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Firebase Auth Users Section - Admin Only */}
                {isCurrentUserAdmin() && (
                  <div style={{ marginTop: '32px' }}>
                    <div style={{
                      padding: '20px',
                      marginBottom: '20px',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                      background: 'var(--light-bg)'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                      }}>
                        <div>
                          <h4 style={{ 
                            margin: 0, 
                            fontSize: '16px', 
                            fontWeight: 600, 
                            color: 'var(--text-dark)' 
                          }}>
                            Firebase Authentication Users
                          </h4>
                          <p style={{ 
                            margin: '4px 0 0 0', 
                            fontSize: '13px', 
                            color: 'var(--text-muted)' 
                          }}>
                            Users from Firebase Auth who need roles assigned
                          </p>
                        </div>
                        <button
                          type="button"
                          className="secondary-btn"
                          onClick={loadAuthUsers}
                          disabled={loadingAuthUsers}
                          style={{ padding: '8px 16px', fontSize: '13px' }}
                        >
                          {loadingAuthUsers ? 'Loading...' : 'Refresh'}
                        </button>
                      </div>

                      {loadingAuthUsers ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          <div style={{ 
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            border: '3px solid var(--border)',
                            borderTopColor: 'var(--primary)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: '8px'
                          }}></div>
                          <p style={{ margin: 0, fontSize: '13px' }}>Loading Firebase Auth users...</p>
                        </div>
                      ) : authUsers.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          <p style={{ margin: 0, fontSize: '13px' }}>
                            No Firebase Auth users found.
                          </p>
                        </div>
                      ) : (
                        <div style={{
                          display: 'grid',
                          gap: '12px'
                        }}>
                          {authUsers
                            .filter(authUser => {
                              // Only show users that don't have a role assigned in Firestore
                              const hasRole = users.some(u => 
                                u.firebaseUid === authUser.uid || 
                                (u.email && authUser.email && u.email.toLowerCase() === authUser.email.toLowerCase())
                              );
                              return !hasRole;
                            })
                            .map((authUser) => {
                              const isAssigning = assigningRole === authUser.uid;
                              return (
                                <div
                                  key={authUser.uid}
                                  style={{
                                    padding: '16px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border)',
                                    background: 'var(--card-bg)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '12px'
                                  }}
                                >
                                  <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                                    <div style={{ 
                                      fontSize: '14px', 
                                      fontWeight: 600, 
                                      color: 'var(--text-dark)',
                                      marginBottom: '4px'
                                    }}>
                                      {authUser.email || authUser.displayName || authUser.uid.substring(0, 8)}
                                    </div>
                                    <div style={{ 
                                      fontSize: '12px', 
                                      color: 'var(--text-muted)',
                                      display: 'flex',
                                      gap: '12px',
                                      flexWrap: 'wrap',
                                      marginTop: '4px'
                                    }}>
                                      <span>UID: {authUser.uid.substring(0, 12)}...</span>
                                      {authUser.emailVerified && (
                                        <span style={{ color: '#10b981' }}>✓ Verified</span>
                                      )}
                                      {authUser.disabled && (
                                        <span style={{ color: '#ef4444' }}>⚠ Disabled</span>
                                      )}
                                    </div>
                                  </div>
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '8px', 
                                    alignItems: 'center',
                                    flexWrap: 'wrap'
                                  }}>
                                    <select
                                      className="settings-select"
                                      value=""
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          handleAssignRole(authUser, e.target.value);
                                          e.target.value = '';
                                        }
                                      }}
                                      disabled={isAssigning}
                                      style={{ 
                                        minWidth: '120px',
                                        padding: '8px 12px',
                                        fontSize: '13px'
                                      }}
                                    >
                                      <option value="">Assign Role...</option>
                                      <option value="sales">Sales</option>
                                      <option value="technician">Technician</option>
                                      {isCurrentUserAdmin() && <option value="admin">Admin</option>}
                                      <option value="owner">Owner</option>
                                    </select>
                                    {isAssigning && (
                                      <div style={{ 
                                        display: 'inline-block',
                                        width: '16px',
                                        height: '16px',
                                        border: '2px solid var(--border)',
                                        borderTopColor: 'var(--primary)',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                      }}></div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      )}

                      {authUsers.length > 0 && authUsers.filter(authUser => {
                        const hasRole = users.some(u => 
                          u.firebaseUid === authUser.uid || 
                          (u.email && authUser.email && u.email.toLowerCase() === authUser.email.toLowerCase())
                        );
                        return !hasRole;
                      }).length === 0 && (
                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                          <p style={{ margin: 0, fontSize: '13px' }}>
                            All Firebase Auth users have roles assigned.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Company Information</h3>
                  <p className="settings-section-subtitle">Update your company details and branch location</p>
                </div>
                
                {companyError && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#dc2626',
                    fontSize: '13px'
                  }}>
                    {companyError}
                  </div>
                )}

                {companySuccess && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    color: '#059669',
                    fontSize: '13px'
                  }}>
                    {companySuccess}
                  </div>
                )}

                <form onSubmit={handleSaveCompanyInfo}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: 'var(--text-dark)' 
                      }}>
                        Company Name *
                      </label>
                      <input
                        type="text"
                        value={companyForm.companyName}
                        onChange={(e) => handleCompanyFormChange('companyName', e.target.value)}
                        placeholder="Enter your company name"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          fontSize: '14px',
                          background: '#fff',
                          color: 'inherit'
                        }}
                        required
                      />
                      <p style={{ 
                        marginTop: '6px', 
                        fontSize: '12px', 
                        color: 'var(--text-muted)' 
                      }}>
                        This name appears in the dashboard greeting
                      </p>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: 'var(--text-dark)' 
                      }}>
                        Branch Location (Address) *
                      </label>
                      <textarea
                        value={companyForm.address}
                        onChange={(e) => handleCompanyFormChange('address', e.target.value)}
                        placeholder="Enter your branch address"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          background: '#fff',
                          color: 'inherit',
                          minHeight: '80px'
                        }}
                        required
                      />
                      <p style={{ 
                        marginTop: '6px', 
                        fontSize: '12px', 
                        color: 'var(--text-muted)' 
                      }}>
                        This address appears in the sidebar and on invoices
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontSize: '13px', 
                          fontWeight: 500, 
                          color: 'var(--text-dark)' 
                        }}>
                          Contact Phone *
                        </label>
                        <input
                          type="tel"
                          value={companyForm.contact}
                          onChange={(e) => handleCompanyFormChange('contact', e.target.value)}
                          placeholder="020 1234 5678"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            fontSize: '14px',
                            background: '#fff',
                            color: 'inherit'
                          }}
                          required
                        />
                      </div>

                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '8px', 
                          fontSize: '13px', 
                          fontWeight: 500, 
                          color: 'var(--text-dark)' 
                        }}>
                          VAT Number
                        </label>
                        <input
                          type="text"
                          value={companyForm.vatNumber}
                          onChange={(e) => handleCompanyFormChange('vatNumber', e.target.value)}
                          placeholder="GB123456789"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            fontSize: '14px',
                            background: '#fff',
                            color: 'inherit'
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: 'var(--text-dark)' 
                      }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={companyForm.email}
                        onChange={(e) => handleCompanyFormChange('email', e.target.value)}
                        placeholder="info@company.com"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid var(--border)',
                          fontSize: '14px',
                          background: '#fff',
                          color: 'inherit'
                        }}
                      />
                    </div>

                    <div style={{
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      background: 'var(--light-bg, #f9fafb)',
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--text-dark)',
                        lineHeight: 1.45,
                        margin: 0,
                      }}>
                        <input
                          type="checkbox"
                          checked={companyForm.whatsappServiceDueRemindersEnabled === true}
                          onChange={(e) => handleCompanyFormChange('whatsappServiceDueRemindersEnabled', e.target.checked)}
                          style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0 }}
                        />
                        <span>
                          <strong>Automatic WhatsApp reminders for next service due</strong>
                          <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 400 }}>
                            Sends approved Meta template messages to customers on their vehicle contact number at 30, 15, 5, and 1 days before the due date by default (override with SERVICE_DUE_REMINDER_DAYS on the server). Requires{' '}
                            <code style={{ fontSize: '11px' }}>SERVICE_DUE_WHATSAPP_TEMPLATE_NAME</code> and Meta credentials on your Cloud Function.
                          </span>
                        </span>
                      </label>
                    </div>

                    <div style={{
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      background: 'var(--light-bg, #f9fafb)',
                      marginTop: '12px',
                    }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--text-dark)',
                        lineHeight: 1.45,
                        margin: 0,
                      }}>
                        <input
                          type="checkbox"
                          checked={companyForm.whatsappMotRemindersEnabled === true}
                          onChange={(e) => handleCompanyFormChange('whatsappMotRemindersEnabled', e.target.checked)}
                          style={{ marginTop: '3px', width: '18px', height: '18px', flexShrink: 0 }}
                        />
                        <span>
                          <strong>Automatic WhatsApp reminders for MOT due</strong>
                          <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 400 }}>
                            Sends the Meta <code style={{ fontSize: '11px' }}>mot_reminder</code> template at 30, 15, 5, and 1 days before MOT expiry by default (override with MOT_REMINDER_DAYS on the server). Uses the vehicle MOT expiry date (or date parsed from MOT status). Requires{' '}
                            <code style={{ fontSize: '11px' }}>MOT_REMINDER_WHATSAPP_TEMPLATE_NAME</code> on the Cloud Function.
                            <span style={{ display: 'block', marginTop: '8px' }}>
                              MOT booking confirmation is sent automatically when an MOT is booked in (template <code style={{ fontSize: '11px' }}>mot_booked_confirmation</code>), if the customer has a mobile number.
                            </span>
                          </span>
                        </span>
                      </label>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-dark)'
                      }}>
                        Work types
                      </label>
                      <p style={{
                        marginBottom: '12px',
                        fontSize: '12px',
                        color: 'var(--text-muted)'
                      }}>
                        Turn on the areas this company does. This updates stages, boards and reports across every site.
                      </p>
                      <div className="company-work-types">
                        {ALL_COMPANY_STAGES.map((stage) => {
                          const checked = (companyForm.enabledStages || []).includes(stage);
                          return (
                            <label key={stage} className="company-work-type">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const current = companyForm.enabledStages || [];
                                  const next = e.target.checked
                                    ? [...current, stage]
                                    : current.filter((item) => item !== stage);
                                  const unique = ALL_COMPANY_STAGES.filter((item) => next.includes(item));
                                  if (!e.target.checked && unique.length === 0) {
                                    setCompanyError('Keep at least one work type turned on.');
                                    return;
                                  }
                                  setCompanyForm((prev) => ({ ...prev, enabledStages: unique }));
                                  setCompanyError('');
                                  setCompanySuccess('');
                                }}
                              />
                              <span>{stage}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: 'var(--text-dark)' 
                      }}>
                        Site Names
                      </label>
                      <p style={{ 
                        marginBottom: '12px', 
                        fontSize: '12px', 
                        color: 'var(--text-muted)' 
                      }}>
                        These are examples. Add more site names that appear in the location picker for vehicles
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {companyForm.siteNames.map((siteName, index) => (
                          <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={siteName}
                              onChange={(e) => {
                                const newSiteNames = [...companyForm.siteNames];
                                newSiteNames[index] = e.target.value;
                                setCompanyForm(prev => ({ ...prev, siteNames: newSiteNames }));
                                setCompanyError('');
                                setCompanySuccess('');
                              }}
                              placeholder="Site name"
                              style={{
                                flex: 1,
                                padding: '10px 12px',
                                borderRadius: '8px',
                                border: '1px solid var(--border)',
                                fontSize: '14px',
                                background: '#fff',
                                color: 'inherit'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newSiteNames = companyForm.siteNames.filter((_, i) => i !== index);
                                setCompanyForm(prev => ({ ...prev, siteNames: newSiteNames }));
                                setCompanyError('');
                                setCompanySuccess('');
                              }}
                              style={{
                                padding: '10px 16px',
                                borderRadius: '8px',
                                border: '1px solid var(--border)',
                                background: '#fff',
                                color: '#dc2626',
                                fontSize: '14px',
                                cursor: 'pointer',
                                fontWeight: 500
                              }}
                              disabled={companyForm.siteNames.length <= 1}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setCompanyForm(prev => ({ 
                              ...prev, 
                              siteNames: [...prev.siteNames, ''] 
                            }));
                            setCompanyError('');
                            setCompanySuccess('');
                          }}
                          style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: '2px dashed var(--border)',
                            background: 'transparent',
                            color: 'var(--primary)',
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontWeight: 500,
                            marginTop: '4px'
                          }}
                        >
                          + Add Site
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--text-dark)' }}>
                          Default technician (new bookings)
                        </label>
                        <select
                          value={companyForm.defaultTechnician}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, defaultTechnician: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            fontSize: '14px',
                            background: '#fff',
                            color: 'inherit'
                          }}
                        >
                          <option value="">None</option>
                          {(garageInfo?.technicians || []).map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 500, color: 'var(--text-dark)' }}>
                          Default site (new bookings)
                        </label>
                        <select
                          value={companyForm.defaultSite}
                          onChange={(e) => setCompanyForm(prev => ({ ...prev, defaultSite: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            fontSize: '14px',
                            background: '#fff',
                            color: 'inherit'
                          }}
                        >
                          <option value="">None</option>
                          {(companyForm.siteNames || []).filter(Boolean).map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontSize: '13px', 
                        fontWeight: 500, 
                        color: 'var(--text-dark)' 
                      }}>
                        Technicians
                      </label>
                      <p style={{ 
                        marginBottom: '12px', 
                        fontSize: '12px', 
                        color: 'var(--text-muted)' 
                      }}>
                        Manage technicians that appear in the booking form dropdown
                      </p>
                      {technicianError && (
                        <div style={{
                          padding: '8px 12px',
                          marginBottom: '12px',
                          borderRadius: '8px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          color: '#dc2626',
                          fontSize: '13px'
                        }}>
                          {technicianError}
                        </div>
                      )}
                      {technicianSuccess && (
                        <div style={{
                          padding: '8px 12px',
                          marginBottom: '12px',
                          borderRadius: '8px',
                          background: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          color: '#059669',
                          fontSize: '13px'
                        }}>
                          {technicianSuccess}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {technicians.length > 0 ? (
                          technicians.map((tech) => (
                            <div key={tech} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <div
                                style={{
                                  flex: 1,
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border)',
                                  fontSize: '14px',
                                  background: '#fff',
                                  color: 'inherit'
                                }}
                              >
                                {tech}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveTechnician(tech)}
                                style={{
                                  padding: '10px 16px',
                                  borderRadius: '8px',
                                  border: '1px solid var(--border)',
                                  background: '#fff',
                                  color: '#dc2626',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  fontWeight: 500
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>
                            No technicians found. Add one below.
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                          <input
                            type="text"
                            value={newTechnicianName}
                            onChange={(e) => {
                              setNewTechnicianName(e.target.value);
                              setTechnicianError('');
                              setTechnicianSuccess('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTechnician(newTechnicianName);
                                setNewTechnicianName('');
                              }
                            }}
                            placeholder="Enter technician name"
                            style={{
                              flex: 1,
                              padding: '10px 12px',
                              borderRadius: '8px',
                              border: '2px dashed var(--border)',
                              fontSize: '14px',
                              background: '#fff',
                              color: 'inherit'
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              handleAddTechnician(newTechnicianName);
                              setNewTechnicianName('');
                            }}
                            style={{
                              padding: '10px 16px',
                              borderRadius: '8px',
                              border: '2px dashed var(--border)',
                              background: 'transparent',
                              color: 'var(--primary)',
                              fontSize: '14px',
                              cursor: 'pointer',
                              fontWeight: 500
                            }}
                          >
                            + Add
                          </button>
                        </div>
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      justifyContent: 'flex-end',
                      paddingTop: '8px',
                      borderTop: '1px solid var(--border)'
                    }}>
                      <button
                        type="button"
                        className="secondary-btn"
                        onClick={() => {
                          setCompanyForm({
                            companyName: garageInfo?.companyName || '',
                            address: garageInfo?.address || '',
                            contact: garageInfo?.contact || '',
                            vatNumber: garageInfo?.vatNumber || '',
                            email: garageInfo?.email || '',
                            siteNames: garageInfo?.siteNames && garageInfo.siteNames.length > 0 
                              ? garageInfo.siteNames 
                              : ['Site A']
                          });
                          setCompanyError('');
                          setCompanySuccess('');
                        }}
                        style={{ padding: '10px 20px', fontSize: '14px' }}
                        disabled={companyLoading}
                      >
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="primary-btn"
                        style={{ padding: '10px 20px', fontSize: '14px' }}
                        disabled={companyLoading}
                      >
                        {companyLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="settings-tab-content active">
              {/* Print Job Card Design */}
              <div className="settings-section-card" style={{ marginBottom: '24px' }}>
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Print Job Card Design</h3>
                  <p className="settings-section-subtitle">Choose a template and customise labels. Saved settings apply when you print a job card from a vehicle.</p>
                </div>
                <div className="settings-section-content">
                  <form onSubmit={handleSaveJobCardTemplate}>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Template style</label>
                      <select
                        value={jobCardTemplateId}
                        onChange={(e) => setJobCardTemplateId(e.target.value)}
                        style={{
                          width: '100%',
                          maxWidth: '320px',
                          padding: '10px 12px',
                          fontSize: '14px',
                          borderRadius: '8px',
                          border: '1px solid var(--border, #e5e5e5)',
                          background: 'var(--input-bg, #fff)',
                          color: 'var(--text, #333)'
                        }}
                      >
                        {JOB_CARD_TEMPLATES.map((t) => (
                          <option key={t.id} value={t.id}>{t.name} – {t.description}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '10px' }}>Customise labels</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                        {(['title', 'vehicle', 'customerName', 'contact', 'status', 'collectionDate', 'technician', 'motStatus', 'workSummary', 'costBreakdown', 'baseCost', 'prepCost', 'warranty', 'miscellaneous', 'totalCost', 'notes']).map((key) => (
                          <div key={key}>
                            <label style={{ fontSize: '11px', color: 'var(--text-muted, #666)', display: 'block', marginBottom: '4px' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}</label>
                            <input
                              type="text"
                              value={jobCardLabels[key] ?? ''}
                              onChange={(e) => setJobCardLabels(prev => ({ ...prev, [key]: e.target.value }))}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                fontSize: '13px',
                                borderRadius: '6px',
                                border: '1px solid var(--border, #e5e5e5)',
                                background: 'var(--input-bg, #fff)',
                                color: 'var(--text, #333)'
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {jobCardSuccess && (
                      <div style={{ padding: '10px 12px', marginBottom: '12px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#059669', fontSize: '13px' }}>
                        {jobCardSuccess}
                      </div>
                    )}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                      <button type="button" className="secondary-btn" onClick={handleOpenJobCardPreview} style={{ padding: '8px 16px', fontSize: '13px' }}>
                        Open print preview (sample)
                      </button>
                      <button type="submit" className="primary-btn" disabled={jobCardSaving} style={{ padding: '8px 16px', fontSize: '13px' }}>
                        {jobCardSaving ? 'Saving...' : 'Save job card template'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Message Templates - Coming Soon */}
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Message Templates</h3>
                  <p className="settings-section-subtitle">Create and manage reusable message templates for customer communications</p>
                </div>
                <div className="settings-section-content">
                  <div className="templates-placeholder" style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#999'
                  }}>
                    <svg 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{
                        width: '64px',
                        height: '64px',
                        margin: '0 auto 20px',
                        opacity: 0.5
                      }}
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                    <h4 style={{ marginBottom: '10px', fontSize: '18px' }}>Templates Coming Soon</h4>
                    <p style={{ fontSize: '14px', maxWidth: '500px', margin: '0 auto' }}>
                      Create custom message templates for vehicle status updates, collection reminders, and customer notifications.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'roles' && canAccessRoles() && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <div>
                    <h3 className="settings-section-title">Role Permissions</h3>
                    <p className="settings-section-subtitle">
                      Configure default page access permissions for each user role
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={handleResetRolePermissions}
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                    >
                      Reset to Defaults
                    </button>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={handleSaveRolePermissions}
                      disabled={loadingRolePermissions}
                      style={{ padding: '8px 16px', fontSize: '13px' }}
                    >
                      {loadingRolePermissions ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>

                {rolePermissionsError && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#dc2626',
                    fontSize: '13px'
                  }}>
                    {rolePermissionsError}
                  </div>
                )}

                {rolePermissionsSuccess && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    color: '#059669',
                    fontSize: '13px'
                  }}>
                    {rolePermissionsSuccess}
                  </div>
                )}

                {loadingRolePermissions && Object.keys(rolePermissions).length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    <div style={{ 
                      display: 'inline-block',
                      width: '24px',
                      height: '24px',
                      border: '3px solid var(--border)',
                      borderTopColor: 'var(--primary)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: '12px'
                    }}></div>
                    <p style={{ margin: 0, fontSize: '14px' }}>Loading role permissions...</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {['owner', ...(isCurrentUserAdmin() ? ['admin'] : []), 'sales', 'technician'].map(role => {
                      const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
                      const roleColors = {
                        owner: '#dc2626',
                        admin: '#ea580c',
                        sales: '#10b981',
                        technician: '#3b82f6'
                      };
                      const rolePerms = rolePermissions[role] || DEFAULT_ROLE_PERMISSIONS[role] || {};

                      return (
                        <div
                          key={role}
                          style={{
                            padding: '20px',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            background: 'var(--light-bg)'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '20px'
                          }}>
                            <span style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: 600,
                              background: `${roleColors[role] || '#6b7280'}20`,
                              color: roleColors[role] || '#6b7280',
                              textTransform: 'capitalize'
                            }}>
                              {roleLabel}
                            </span>
                            <span style={{
                              fontSize: '13px',
                              color: 'var(--text-muted)'
                            }}>
                              Default permissions for {roleLabel} role
                            </span>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '12px'
                          }}>
                            {Object.values(PAGES).map(page => {
                              const hasAccess = rolePerms[page] === true;
                              const pageLabel = getPageLabel(page);

                              return (
                                <label
                                  key={page}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    background: hasAccess ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    border: `1px solid ${hasAccess ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={hasAccess}
                                    onChange={() => handleRolePermissionToggle(role, page)}
                                    style={{
                                      width: '18px',
                                      height: '18px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                  <span style={{
                                    fontSize: '13px',
                                    color: 'var(--text-dark)',
                                    flex: 1,
                                    fontWeight: hasAccess ? 500 : 400
                                  }}>
                                    {pageLabel}
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          {/* Special permissions */}
                          <div style={{
                            marginTop: '20px',
                            paddingTop: '20px',
                            borderTop: '1px solid var(--border)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                          }}>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px',
                                borderRadius: '8px',
                                background: rolePerms.canManageUsers ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                border: `1px solid ${rolePerms.canManageUsers ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={rolePerms.canManageUsers === true}
                                onChange={() => handleRolePermissionToggle(role, 'canManageUsers')}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer'
                                }}
                              />
                              <span style={{
                                fontSize: '13px',
                                color: 'var(--text-dark)',
                                fontWeight: 500
                              }}>
                                Can Manage Users
                              </span>
                            </label>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px',
                                borderRadius: '8px',
                                background: rolePerms.canAccessSettings ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                border: `1px solid ${rolePerms.canAccessSettings ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                                cursor: 'pointer'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={rolePerms.canAccessSettings === true}
                                onChange={() => handleRolePermissionToggle(role, 'canAccessSettings')}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer'
                                }}
                              />
                              <span style={{
                                fontSize: '13px',
                                color: 'var(--text-dark)',
                                fontWeight: 500
                              }}>
                                Can Access Settings
                              </span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div className="settings-tab-content active">
              <div className="settings-section-card audit-log">
                <div className="settings-section-header">
                  <div>
                    <h3 className="settings-section-title">Audit Log</h3>
                    <p className="settings-section-subtitle">Who changed vehicles, invoices, and settings — and when</p>
                  </div>
                  {!auditLogLoading && auditLog.length > 0 && (
                    <span className="audit-log-count">
                      {auditLog.length} {auditLog.length === 1 ? 'change' : 'changes'}
                    </span>
                  )}
                </div>

                {auditLogLoading ? (
                  <ul className="audit-log-feed" aria-busy="true" aria-label="Loading audit log">
                    {[0, 1, 2, 3].map((i) => (
                      <li key={i} className="audit-log-row is-loading">
                        <span className="audit-log-skeleton" />
                      </li>
                    ))}
                  </ul>
                ) : auditLog.length === 0 ? (
                  <p className="audit-log-empty">Nothing recorded yet. Edits will appear here.</p>
                ) : (
                  <ul className="audit-log-feed">
                    {auditLog.map((entry) => {
                      const time = formatAuditTime(entry.createdAt);
                      const actionKey = (entry.action || 'update').replace(/[^a-z_]/gi, '');
                      const entity = formatEntityDisplay(entry.entityType, entry.entityId, entry.details);
                      const detailsLine = formatAuditLogDetails(entry.details, entry.entityType, entry.action);
                      const who = entry.userEmail || entry.userId || '—';
                      return (
                        <li key={entry.id} className={`audit-log-row audit-log-row--${actionKey}`}>
                          <div className="audit-log-meta">
                            <span className="audit-log-action">{getActionLabel(entry.action)}</span>
                            <time className="audit-log-when" dateTime={entry.createdAt || undefined}>
                              {time.full}
                              {time.relative && time.relative !== time.full ? ` · ${time.relative}` : ''}
                            </time>
                          </div>
                          <p className="audit-log-entity">
                            <span className="audit-log-kind">{entity.kind}</span>
                            {entity.ref ? (
                              <span className="audit-log-ref">{entity.ref}</span>
                            ) : null}
                          </p>
                          <p className="audit-log-who">{who}</p>
                          {detailsLine && detailsLine !== '—' ? (
                            <p className="audit-log-details">{detailsLine}</p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Change Password</h3>
                  <p className="settings-section-subtitle">Update your account password</p>
                </div>
                
                {passwordChangeError && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    color: '#dc2626',
                    fontSize: '13px'
                  }}>
                    {passwordChangeError}
                  </div>
                )}

                {passwordChangeSuccess && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    color: '#059669',
                    fontSize: '13px'
                  }}>
                    {passwordChangeSuccess}
                  </div>
                )}

                <form onSubmit={handlePasswordChange} style={{ marginTop: '24px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="currentPassword" style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '13px', 
                      fontWeight: 500, 
                      color: 'var(--text-dark)' 
                    }}>
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      placeholder="Enter your current password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => {
                        setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }));
                        setPasswordChangeError('');
                      }}
                      required
                      disabled={passwordChangeLoading}
                      autoComplete="current-password"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '14px',
                        background: '#fff',
                        color: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="newPassword" style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '13px', 
                      fontWeight: 500, 
                      color: 'var(--text-dark)' 
                    }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      placeholder="Enter your new password (min. 6 characters)"
                      value={passwordForm.newPassword}
                      onChange={(e) => {
                        setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }));
                        setPasswordChangeError('');
                      }}
                      required
                      disabled={passwordChangeLoading}
                      autoComplete="new-password"
                      minLength={6}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '14px',
                        background: '#fff',
                        color: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label htmlFor="confirmPassword" style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: '13px', 
                      fontWeight: 500, 
                      color: 'var(--text-dark)' 
                    }}>
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      placeholder="Confirm your new password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => {
                        setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }));
                        setPasswordChangeError('');
                      }}
                      required
                      disabled={passwordChangeLoading}
                      autoComplete="new-password"
                      minLength={6}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        fontSize: '14px',
                        background: '#fff',
                        color: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => {
                        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setPasswordChangeError('');
                        setPasswordChangeSuccess('');
                      }}
                      disabled={passwordChangeLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="primary-btn"
                      disabled={passwordChangeLoading}
                    >
                      {passwordChangeLoading ? 'Changing Password...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'whatsapp-templates' && canViewWhatsAppTemplatesTab && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">WhatsApp Templates</h3>
                  <p className="settings-section-subtitle">
                    Approved Meta Business message templates available for your WhatsApp number.
                    Templates are required when messaging customers outside the 24-hour window.
                  </p>
                </div>

                {waTemplatesLoading && (
                  <div style={{ padding: '32px', textAlign: 'center', color: '#888' }}>Loading templates…</div>
                )}

                {waTemplatesError && (
                  <div style={{ padding: '16px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', color: '#EF4444', marginBottom: '16px' }}>
                    {waTemplatesError}
                  </div>
                )}

                {!waTemplatesLoading && !waTemplatesError && waTemplates.length === 0 && (
                  <div style={{ padding: '32px', textAlign: 'center', color: '#888' }}>No templates found.</div>
                )}

                {!waTemplatesLoading && waTemplates.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {waTemplates.map((tpl, idx) => {
                      const bodyComp = (tpl.components || []).find(c => c.type === 'BODY');
                      const headerComp = (tpl.components || []).find(c => c.type === 'HEADER');
                      const footerComp = (tpl.components || []).find(c => c.type === 'FOOTER');
                      const buttonsComp = (tpl.components || []).find(c => c.type === 'BUTTONS');
                      const statusColor = tpl.status === 'APPROVED' ? '#10B981' : tpl.status === 'PENDING' ? '#F59E0B' : '#EF4444';
                      return (
                        <div key={idx} style={{
                          border: '1px solid rgba(0,0,0,0.1)',
                          borderRadius: '12px',
                          padding: '16px',
                          background: 'rgba(0,0,0,0.02)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px', fontFamily: 'monospace', background: 'rgba(0,0,0,0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                              {tpl.name}
                            </span>
                            <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '12px', background: `${statusColor}20`, color: statusColor, fontWeight: 600 }}>
                              {tpl.status}
                            </span>
                            <span style={{ fontSize: '11px', color: '#888', padding: '2px 8px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)' }}>
                              {tpl.category}
                            </span>
                            <span style={{ fontSize: '11px', color: '#888', padding: '2px 8px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)' }}>
                              {tpl.language}
                            </span>
                          </div>

                          <div style={{ background: '#DCF8C6', borderRadius: '8px', padding: '12px', maxWidth: '420px', fontSize: '13px', lineHeight: '1.5' }}>
                            {headerComp && headerComp.text && (
                              <div style={{ fontWeight: 700, marginBottom: '6px' }}>{headerComp.text}</div>
                            )}
                            {bodyComp && bodyComp.text && (
                              <div style={{ whiteSpace: 'pre-wrap' }}>{bodyComp.text}</div>
                            )}
                            {footerComp && footerComp.text && (
                              <div style={{ color: '#888', fontSize: '11px', marginTop: '6px' }}>{footerComp.text}</div>
                            )}
                            {buttonsComp && Array.isArray(buttonsComp.buttons) && (
                              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {buttonsComp.buttons.map((btn, bi) => (
                                  <span key={bi} style={{ fontSize: '12px', color: '#075E54', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '6px', width: '100%' }}>
                                    {btn.text}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ marginTop: '16px', padding: '12px 16px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px', fontSize: '13px', color: '#3B82F6' }}>
                  To create or edit templates, go to{' '}
                  <a href="https://business.facebook.com/wa/manage/message-templates/" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', fontWeight: 600 }}>
                    Meta Business Manager → Message Templates
                  </a>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'help' && (
            <div className="settings-tab-content active">
              <div className="settings-section-card">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Help & FAQ</h3>
                  <p className="settings-section-subtitle">Frequently asked questions and helpful guides</p>
                </div>
                
                <div className="faq-container">
                  <div className="faq-item">
                    <h4 className="faq-question">How do I upload my company logo?</h4>
                    <div className="faq-answer">
                      <p>1. Go to <strong>Settings</strong> → <strong>CRM Settings</strong> tab</p>
                      <p>2. In the <strong>Branding</strong> section, click <strong>"Upload Logo"</strong></p>
                      <p>3. Select an image file (PNG, JPG) up to 2MB in size</p>
                      <p>4. Your logo will appear in the sidebar and throughout the application</p>
                      <p><em>Tip: Use a square logo (1:1 ratio) for best results</em></p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I change the application colors?</h4>
                    <div className="faq-answer">
                      <p>1. Navigate to <strong>Settings</strong> → <strong>CRM Settings</strong></p>
                      <p>2. Look for the <strong>Color Scheme</strong> or <strong>Branding</strong> section</p>
                      <p>3. Select your preferred primary and secondary colors</p>
                      <p>4. Changes will be applied immediately across the application</p>
                      <p><em>Note: Choose colors that provide good contrast for readability</em></p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I add a new vehicle to the system?</h4>
                    <div className="faq-answer">
                      <p>1. Click the <strong>"Book"</strong> button in the header</p>
                      <p>2. Enter the vehicle registration number</p>
                      <p>3. Fill in vehicle details, customer information, and service requirements</p>
                      <p>4. Set the initial status (Workshop, MOT, etc.)</p>
                      <p>5. Click <strong>"Save"</strong> to add the vehicle to your garage</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I update vehicle costs and pricing?</h4>
                    <div className="faq-answer">
                      <p>1. Click on any vehicle card to open the details modal</p>
                      <p>2. Use the <strong>edit buttons (✎)</strong> next to cost fields</p>
                      <p>3. Update costs for parts, labor, prep work, warranty, etc.</p>
                      <p>4. Set sale prices for vehicles moving to sales</p>
                      <p>5. All financial calculations update automatically</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I move vehicles between different stages?</h4>
                    <div className="faq-answer">
                      <p>1. Open the vehicle details by clicking on a vehicle card</p>
                      <p>2. Use the status buttons: <strong>Pass MOT</strong>, <strong>Fail MOT</strong>, <strong>Mark as Sold</strong></p>
                      <p>3. Or drag and drop vehicles between columns in the garage view</p>
                      <p>4. Vehicle status will update and move to the appropriate section</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I track payments and invoices?</h4>
                    <div className="faq-answer">
                      <p>1. Go to the <strong>Finance</strong> page from the sidebar</p>
                      <p>2. View <strong>Total Billed</strong>, <strong>Total Received</strong>, and <strong>Outstanding</strong> amounts</p>
                      <p>3. Switch between <strong>Paid Vehicles</strong> and <strong>Outstanding</strong> tabs</p>
                      <p>4. Click on individual vehicles to update payment status</p>
                      <p>5. Use the <strong>Invoices</strong> page to generate and manage invoices</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I use the calendar and scheduling?</h4>
                    <div className="faq-answer">
                      <p>1. Navigate to the <strong>Calendar</strong> page</p>
                      <p>2. View vehicles due for collection or MOT by date</p>
                      <p>3. Click on any date to see scheduled vehicles</p>
                      <p>4. Use the mini calendar on the dashboard for quick date navigation</p>
                      <p>5. Set collection dates when booking vehicles</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I generate sales reports?</h4>
                    <div className="faq-answer">
                      <p>1. Go to <strong>Sales</strong> → <strong>Sales Report</strong></p>
                      <p>2. View financial breakdown including costs, profits, and revenue</p>
                      <p>3. Click on summary cards to filter vehicles by status</p>
                      <p>4. Sort columns by clicking on headers (Cost, Profit, etc.)</p>
                      <p>5. Use the data to analyze business performance</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I backup my data?</h4>
                    <div className="faq-answer">
                      <p>1. Use the <strong>Export</strong> button in the header</p>
                      <p>2. This will download your vehicle data as a backup file</p>
                      <p>3. Store backup files in a safe location</p>
                      <p>4. Regular backups are recommended for data security</p>
                      <p><em>Note: Backups include all vehicle, customer, and financial data</em></p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I search for specific vehicles?</h4>
                    <div className="faq-answer">
                      <p>1. Use the search functionality in <strong>Garage Vehicles</strong> page</p>
                      <p>2. Search by registration number, vehicle make/model, or customer name</p>
                      <p>3. Use status filters to narrow down results (MOT, Sales, etc.)</p>
                      <p>4. Click on any vehicle card to view full details</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">I've deleted a vehicle by mistake. How do I get it back?</h4>
                    <div className="faq-answer">
                      <p>1. Go to <strong>Archive</strong> from the sidebar</p>
                      <p>2. Find the vehicle in the archived list</p>
                      <p>3. Click <strong>Restore</strong> in the toolbar, then click the vehicle card to bring it back to your active garage</p>
                      <p>4. The vehicle will return with its previous status and data</p>
                      <p><em>Tip: Deleted vehicles are moved to Archive so you can restore them if removed by mistake</em></p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">What should I do if I encounter an error?</h4>
                    <div className="faq-answer">
                      <p>1. Try refreshing the page (Ctrl+F5 or Cmd+Shift+R)</p>
                      <p>2. Check your internet connection</p>
                      <p>3. Clear your browser cache and cookies</p>
                      <p>4. If the problem persists, contact technical support</p>
                      <p>5. Include error messages and steps to reproduce the issue</p>
                    </div>
                  </div>

                  <div className="faq-item">
                    <h4 className="faq-question">How do I customize my garage information?</h4>
                    <div className="faq-answer">
                      <p>1. The garage information is displayed in the sidebar</p>
                      <p>2. Go to <strong>Settings</strong> → <strong>CRM Settings</strong></p>
                      <p>3. Update your garage name, address, and contact details</p>
                      <p>4. Changes will be reflected throughout the application</p>
                      <p>5. This information appears on invoices and reports</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;

